import EditIcon from '@mui/icons-material/Edit';
import SmsIcon from '@mui/icons-material/Sms';
import { Box, Stack, FormControlLabel, Switch, Typography } from "@mui/material";
import { useEffect, useState, useCallback, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import { isLocalDev } from "../../../utils/environment";
import { AppointmentService } from "../../../service/appointmentService";
import { toast } from "react-toastify";
import { DEFAULT_PAGE_SIZE } from "../../../utils";
import useDialogModal from "../../../hooks/useDialogModal";
import useAbortController from "../../../hooks/useAbortController";
import { useCan } from "../../../hooks/useCan";
import ConfirmationDialog from "../../ui/confirmationDialog";
import validLookup from "../../../utils/validLookup";
import EnhancedTable from "../../enhancedTable/EnhancedTable";
import { getAppointmentColumns } from "./appointment/columns";
import ChangeStatusDialog from "./appointment/changeStatusDialog";

export default function AppointmentTab({ customer }) {
    const can = useCan();
    const navigate = useNavigate();
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [signal, abortRequest] = useAbortController();
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        loading: false,
    });

    const [ConfirmationDialogComp, showConfirmationDialog, closeConfirmationDialog] =
        useDialogModal(ConfirmationDialog);

    // Fetch appointments with pagination
    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        const from = pageSize * pageIndex;
        updateState({ loading: true });
        
        const options = showAllHistory ? { includeAllPast: true } : undefined;
        
        AppointmentService.getByCustomerId(customer.customer.id, signal, options)
            .then(result => {
                const { code, data } = result;
                if (code === 0) {
                    // Since backend doesn't support pagination yet, we'll do client-side pagination
                    const paginatedData = data.slice(from, from + pageSize);
                    updateState({
                        data: paginatedData,
                        totalRowCount: data.length,
                        pageCount: Math.ceil(data.length / pageSize),
                    });
                }
                updateState({ loading: false });
            })
            .catch(error => {
                if (error?.message !== 'canceled' && error !== 'canceled') {
                    toast.error("Failed to load appointments");
                    
                    if (isLocalDev) {
                        console.error('Error loading appointments:', error);
                    }
                }
                updateState({ loading: false });
            });
    }, [customer, signal, showAllHistory]);

    useEffect(() => {
        return () => {
            abortRequest();
        }
    }, [abortRequest]);

    const handleBookAppointment = () => {
        // Navigate to the scheduler page with customer context
        // The scheduler will allow visual booking with practitioner availability
        navigate('/appointments', { 
            state: { 
                customerId: customer.customer.id,
                customerName: `${customer.customer.firstName} ${customer.customer.lastName}`
            } 
        });
    }

    const handleAppointmentDelete = (rowids) => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                try {
                    updateState({ loading: true });

                    // Delete all selected appointments
                    for (const id of rowids) {
                        await AppointmentService.deleteAppointment(id);
                    }

                    toast.success(`${rowids.length} appointment(s) canceled successfully`);
                    
                    // Refresh the table
                    fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0, filters: [], sortBy: [] });
                } catch (error) {
                    toast.error("Failed to cancel appointment(s)");
                    
                    if (isLocalDev) {
                        console.error('Error deleting appointments:', error);
                    }
                } finally {
                    updateState({ loading: false });
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Cancel Appointment(s)',
            message: `Are you sure you want to cancel ${rowids.length} appointment(s)?`,
        });
    }

    const handleChangeStatus = (appointment) => {
        setSelectedAppointment(appointment);
        setStatusDialogOpen(true);
    };

    const handleStatusUpdate = async (appointmentId, newStatus) => {
        try {
            updateState({ loading: true });
            const result = await AppointmentService.updateStatus({
                id: appointmentId,
                status: newStatus
            });

            if (result.code === 0) {
                toast.success('Appointment status updated successfully');
                // Refresh the table
                fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0, filters: [], sortBy: [] });
            }
        } catch (error) {
            toast.error("Failed to update appointment status");
            if (isLocalDev) {
                console.error('Error updating status:', error);
            }
            throw error; // Re-throw to let dialog handle it
        } finally {
            updateState({ loading: false });
        }
    };

    const handleSendReminder = async (appointment) => {
        try {
            updateState({ loading: true });
            const result = await AppointmentService.sendReminder(appointment.id);

            if (result.code === 0) {
                toast.success(`Reminder sent to ${appointment.Customer?.firstName || 'customer'}`);
            }
        } catch (error) {
            toast.error("Failed to send reminder");
            if (isLocalDev) {
                console.error('Error sending reminder:', error);
            }
        } finally {
            updateState({ loading: false });
        }
    };

    return (
        <Box display="flex" flexDirection="column">
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <FormControlLabel
                    control={
                        <Switch 
                            checked={showAllHistory} 
                            onChange={(e) => setShowAllHistory(e.target.checked)}
                            size="small"
                        />
                    }
                    label="Show All History"
                />
            </Stack>

            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                {showAllHistory ? 'All Appointments' : 'Recent & Upcoming Appointments'}
            </Typography>

            <EnhancedTable
                columns={getAppointmentColumns()}
                data={state.data}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                loading={state.loading}
                selectionMode="multi"
                deleteRecordHandler={can.action('scheduling', 'delete-appointment') ? handleAppointmentDelete : null}
                addRecordHandler={can.action('scheduling', 'create-appointment') ? handleBookAppointment : undefined}
                actions={[
                    // Change Status - requires scheduling permission
                    ...(can.action('scheduling', 'edit-appointment') ? [{
                        icon: <EditIcon />,
                        onClick: handleChangeStatus,
                        tooltip: 'Change Status'
                    }] : []),
                    // Send Reminder - requires messaging.send permission
                    ...(can.action('messaging', 'send') ? [{
                        icon: <SmsIcon />,
                        onClick: handleSendReminder,
                        tooltip: 'Send Reminder'
                    }] : [])
                ]}
            />

            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} 
            />
            <ChangeStatusDialog
                open={statusDialogOpen}
                onClose={() => setStatusDialogOpen(false)}
                appointment={selectedAppointment}
                onStatusChange={handleStatusUpdate}
            />
        </Box>
    )
}